import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Search, Plus, Eye, EyeOff, Star, Trash2, Upload, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { CategoryEditor } from "./CategoryEditor";
import { CategorySlugTools } from "./CategorySlugTools";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";

function DeleteItem({ coll, item, all, onChanged }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("");
  const noun = coll === "categories" ? "category" : "brand";
  const has = item.product_count > 0;
  const go = async () => {
    try {
      const r = await api.delete(`/admin/${coll}/${item.id}`, { params: has ? { reassign_to: target } : {} });
      toast.success(has ? `Deleted — ${r.data.moved} product(s) moved` : "Deleted");
      setOpen(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <>
      <button data-testid={`catalog-delete-${item.id}`} title={`Delete ${noun}`} onClick={() => setOpen(true)}
        className="ml-2 text-slate-400 hover:text-red-500 align-middle"><Trash2 size={16} /></button>
      <ConfirmDelete open={open} title={`Delete ${noun} “${item.name}”?`} onCancel={() => setOpen(false)} onConfirm={go}
        disabled={has && !target} confirmLabel={has ? "Move products & delete" : "Delete permanently"}
        description={has ? `It has ${item.product_count} product(s). Pick where to move them — the old URL and supplier name will point there too.` : "This can't be undone."}>
        {has && (
          <select value={target} onChange={(e) => setTarget(e.target.value)} className="h-9 w-full rounded-md border bg-white px-2 text-sm" data-testid={`catalog-reassign-${item.id}`}>
            <option value="">Move products to…</option>
            {all.filter((x) => x.id !== item.id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        )}
      </ConfirmDelete>
    </>
  );
}

function Row({ coll, item, all, onChanged, onEdit }) {
  const [d, setD] = useState({ name: item.name, sort_order: item.sort_order, image_url: item.image_url || "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const dirty = d.name !== item.name || Number(d.sort_order) !== item.sort_order || d.image_url !== (item.image_url || "");

  const patch = async (body, label) => {
    try { await api.put(`/admin/${coll}/${item.id}`, body); onChanged(); if (label) toast.success(label); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const saveRow = async () => { setSaving(true); await patch({ name: d.name, sort_order: Number(d.sort_order), image_url: d.image_url }, "Saved"); setSaving(false); };
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "image");
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setD((s) => ({ ...s, image_url: data.url }));
      await patch({ image_url: data.url }, "Image uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <tr data-testid={`catalog-row-${item.id}`} className="border-t hover:bg-slate-50">
      <td className="p-2"><Input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} className="h-9" data-testid={`catalog-name-${item.id}`} />
        <div className="text-[11px] font-mono text-muted-foreground mt-0.5 px-1" data-testid={`catalog-slug-${item.id}`}>/{coll === "categories" ? "category" : "brand"}/{coll === "categories" ? item.slug : encodeURIComponent(item.name)}</div></td>
      <td className="p-2 text-center text-sm text-muted-foreground">{item.product_count}</td>
      <td className="p-2 w-20"><Input type="number" value={d.sort_order} onChange={(e) => setD({ ...d, sort_order: e.target.value })} className="h-9" /></td>
      <td className="p-2">
        <div className="flex items-center gap-1.5">
          <span className="w-9 h-9 shrink-0 rounded-md border bg-slate-100 grid place-items-center overflow-hidden">
            {d.image_url ? <img src={d.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : null}
          </span>
          <Input value={d.image_url} onChange={(e) => setD({ ...d, image_url: e.target.value })} placeholder="Image URL or upload →" className="h-9" />
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} />
          <Button size="sm" variant="outline" className="h-9 shrink-0 px-2.5" disabled={uploading}
            onClick={() => fileRef.current?.click()} data-testid={`catalog-upload-${item.id}`} title="Upload image">
            <Upload size={15} />
          </Button>
        </div>
      </td>
      <td className="p-2 text-center">
        <Switch data-testid={`catalog-visible-${item.id}`} checked={item.visible}
          onCheckedChange={(c) => patch({ visible: c }, c ? "Now visible" : "Hidden from storefront")} />
      </td>
      <td className="p-2 text-center">
        <button data-testid={`catalog-featured-${item.id}`} onClick={() => patch({ featured: !item.featured })}
          className={item.featured ? "text-accent" : "text-slate-300 hover:text-slate-400"}><Star size={18} fill={item.featured ? "currentColor" : "none"} /></button>
      </td>
      <td className="p-2 text-center">
        <Switch data-testid={`catalog-homepage-${item.id}`} checked={!!item.homepage}
          onCheckedChange={(c) => patch({ homepage: c }, c ? "Added to homepage block" : "Removed from homepage block")} />
      </td>
      <td className="p-2 text-right whitespace-nowrap">
        <Button size="sm" variant="outline" disabled={!dirty || saving} onClick={saveRow} data-testid={`catalog-save-${item.id}`}>Save</Button>
        {coll === "categories" && (
          <Button size="sm" variant="outline" className="ml-1.5" onClick={() => onEdit(item.id)} data-testid={`catalog-edit-${item.id}`} title="Slug, SEO, parent"><Settings2 size={14} /></Button>
        )}
        <DeleteItem coll={coll} item={item} all={all} onChanged={onChanged} />
      </td>
    </tr>
  );
}

export default function CatalogTab() {
  const [tab, setTab] = useState("categories");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [show, setShow] = useState("all");

  const load = useCallback(() => { api.get(`/admin/${tab}`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [tab]);
  useEffect(() => { load(); setQ(""); setShow("all"); }, [load]);

  const add = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post(`/admin/${tab}`, { name: newName.trim() });
      if (data.duplicate) toast.info(`“${data.item.name}” already exists`);
      else toast.success(`Created “${data.item.name}”`);
      setNewName(""); load();
    } catch { toast.error("Could not create"); }
  };

  const isVis = (i) => i.visible !== false;
  const filtered = items.filter((i) => (!q || i.name.toLowerCase().includes(q.toLowerCase()))
    && (show === "all" || (show === "hidden" ? !isVis(i) : isVis(i))));
  const visibleCount = items.filter(isVis).length;

  return (
    <div>
      <h1 className="text-3xl font-extrabold mb-1">Categories &amp; Brands</h1>
      <p className="text-muted-foreground mb-6">Control what appears on the storefront. Hide noisy entries, feature key ones, set the order and add images.</p>

      <div className="flex gap-2 mb-5 border-b">
        {[["categories", "Categories"], ["brands", "Brands"]].map(([id, label]) => (
          <button key={id} data-testid={`catalog-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === id ? "border-accent text-steel" : "border-transparent text-muted-foreground hover:text-steel"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${tab}…`} className="pl-9" />
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Add new {tab === "categories" ? "category" : "brand"}</Label>
            <Input data-testid="catalog-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Name" className="w-56" /></div>
          <Button data-testid="catalog-add" onClick={add} className="bg-steel text-white gap-1"><Plus size={16} /> Add</Button>
        </div>
      </div>

      <CategorySlugTools coll={tab} categories={items} onChanged={load} />

      <div className="text-xs text-muted-foreground mb-2 flex flex-wrap items-center gap-2">
        {[["all", `All (${items.length})`, null], ["visible", `${visibleCount} visible`, Eye], ["hidden", `${items.length - visibleCount} hidden`, EyeOff]].map(([id, label, Ic]) => (
          <button key={id} type="button" onClick={() => setShow(id)} data-testid={`catalog-show-${id}`}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full border transition-colors ${show === id ? "bg-steel text-white border-steel" : "bg-white hover:border-accent"}`}>
            {Ic && <Ic size={13} />} {label}
          </button>))}
        <span>· showing {Math.min(filtered.length, 300)} of {filtered.length}</span>
      </div>

      <div className="rounded-xl border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="p-2">Name</th><th className="p-2 text-center">Products</th><th className="p-2">Order</th><th className="p-2">Image URL</th><th className="p-2 text-center">Visible</th><th className="p-2 text-center">Featured</th><th className="p-2 text-center">Homepage</th><th className="p-2"></th></tr>
          </thead>
          <tbody>
            {filtered.slice(0, 300).map((i) => <Row key={i.id} coll={tab} item={i} all={items} onChanged={load} onEdit={setEditing} />)}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="p-6 text-center text-muted-foreground">Nothing found.</p>}
      </div>
      {editing && <CategoryEditor id={editing} categories={items} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
